import { useAuth0 } from '@auth0/auth0-react';
import { useQuery } from '@tanstack/react-query';

import { DOMAIN, LoggerFactory, FEATURE } from 'Services/Logger';

const logger = LoggerFactory.getLogger(DOMAIN.CONSUMER_ENABLEMENT);

/**
 * @requires Auth0 session - This hook depends on the user being authenticated through Auth0.
 *
 * @returns {boolean} Validate if user has a valid session.
 * Returns `false` if the user is not authenticated or has expired session.
 */
export const useValidateSession = (): boolean => {
  const { isAuthenticated, getIdTokenClaims } = useAuth0();

  const validateSession = async (): Promise<boolean> => {
    if (isAuthenticated) {
      try {
        const claims = await getIdTokenClaims();
        const isTokenExpired = claims && claims.exp && claims.exp * 1000 < Date.now();

        return !isTokenExpired;
      } catch (error) {
        logger.error(
          'Failed validateSession with token claims',
          `${DOMAIN.CONSUMER_ENABLEMENT}.${FEATURE.AUTHENTICATION}`,
          {
            error,
          },
        );
        return false;
      }
    }
    return false;
  };

  const { data: isValidSession } = useQuery<boolean>(['AUTH_VALID_SESSION'], validateSession);

  return isValidSession ?? false;
};
